<template>
  <div class="kunden-container">
    <div class="page-header-bar">
      <div class="header-left">
        <div class="header-icon"><i class="fas fa-users"></i></div>
        <div>
          <h2 class="page-title">Kunden</h2>
          <p class="page-subtitle">{{ totalCount }} Einträge<span v-if="filterCity"> · gefiltert nach Ort: <strong>{{ filterCity }}</strong></span><span v-if="filterPostalCode"> · PLZ: <strong>{{ filterPostalCode }}</strong></span></p>
        </div>
      </div>
      <div class="header-right">
        <button v-if="filterCity || filterPostalCode" class="btn-clear" @click="clearFilters">
          <i class="fas fa-times"></i> Filter zurücksetzen
        </button>
        <button class="btn-new" @click="createNewKunde">
          <i class="fas fa-plus"></i> Neuer Kunde
        </button>
      </div>
    </div>

    <DataTable
        v-if="processedKunden.length >= 0"
        title="Kunden"
        :columns="columns"
        :data="processedKunden"
        :loading="loading"
        :current-page="currentPage"
        :total-pages="totalPages"
        :total-count="totalCount"
        @search="handleSearch"
        @sort="handleSort"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
    >
      <template #name="{ item }">
        <router-link :to="`/kunden/${item.id}`" class="customer-link">
          <strong>{{ item.name }}</strong>
        </router-link>
      </template>

      <template #postal_code="{ value }">
        <a v-if="value && value !== '-'" href="#" class="filter-link" @click.prevent="filterByPostalCode(value)">{{ value }}</a>
        <span v-else>-</span>
      </template>

      <template #city="{ value }">
        <a v-if="value && value !== '-'" href="#" class="filter-link" @click.prevent="filterByCity(value)">{{ value }}</a>
        <span v-else>-</span>
      </template>

      <!-- Custom column templates -->
      <template #status="{ value }">
        <span :class="value === 1 ? 'badge bg-success' : 'badge bg-danger'">
          {{ value === 1 ? 'Aktiv' : 'Inaktiv' }}
        </span>
      </template>

      <template #website="{ value }">
        <a v-if="value" :href="value" target="_blank" class="text-decoration-none">
          <i class="fas fa-external-link-alt"></i>
        </a>
        <span v-else>-</span>
      </template>

      <template #email="{ value }">
        <a v-if="value" :href="`mailto:${value}`" class="text-decoration-none">
          {{ value }}
        </a>
        <span v-else>-</span>
      </template>
    </DataTable>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DataTable from '../components/DataTable.vue'

export default {
  name: 'Kunden',

  components: {
    DataTable
  },

  data() {
    return {
      rawKunden: [], // Store the raw API data
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      searchQuery: '',
      sortField: '',
      sortDirection: 'asc',
      perPage: 25,
      filterCity: '',
      filterPostalCode: '',

      columns: [
        { key: 'customer_number', label: 'Kunden-Nr.', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'name_zusatz', label: 'Zusatz', sortable: true },
        { key: 'branche', label: 'Branche', sortable: true },
        { key: 'street', label: 'Straße', sortable: false },
        { key: 'postal_code', label: 'PLZ', sortable: true },
        { key: 'city', label: 'Ort', sortable: true },
        { key: 'phone', label: 'Telefon', sortable: false },
        { key: 'email', label: 'E-Mail', sortable: false }
      ]
    }
  },

  computed: {
    ...mapGetters(['loading']),

    // Transform the raw API data to match the column keys with separated address fields
    processedKunden() {
      return this.rawKunden.map(kunde => {
        return {
          id: kunde.ID_kunde,
          name: kunde.k_name,
          name_zusatz: kunde.k_namezusatz,
          branche: kunde.k_branche,
          street: kunde.k_anschrift || '',
          postal_code: kunde.k_plz || '',
          city: kunde.k_ort || '',
          country: kunde.k_land || '',
          phone: kunde.k_tel,
          email: kunde.k_mail,
          website: kunde.k_page,
          status: kunde.k_status,
          // Include all fields in a searchable format for global text search
          _searchable: this.createSearchableText(kunde)
        }
      })
    }
  },

  mounted() {
    this.applyRouteFilters()
    this.loadKunden()
  },

  watch: {
    '$route.query'() {
      this.applyRouteFilters()
      this.currentPage = 1
      this.loadKunden()
    }
  },

  methods: {
    ...mapActions(['fetchData']),

    createNewKunde() {
      this.$router.push('/kunden/new')
    },

    applyRouteFilters() {
      this.filterCity = this.$route.query.city || ''
      this.filterPostalCode = this.$route.query.postal_code || ''
    },

    filterByCity(city) {
      this.$router.push({ path: '/kunden', query: { ...this.$route.query, city } })
    },

    filterByPostalCode(postal_code) {
      this.$router.push({ path: '/kunden', query: { ...this.$route.query, postal_code } })
    },

    clearFilters() {
      this.$router.push({ path: '/kunden', query: {} })
    },

    // Create a combined text field for global text search
    createSearchableText(kunde) {
      const fields = [
        kunde.k_name,
        kunde.k_namezusatz,
        kunde.k_branche,
        kunde.k_anschrift,
        kunde.k_plz,
        kunde.k_ort,
        kunde.k_land,
        kunde.k_tel,
        kunde.k_mail,
        kunde.k_page
      ]

      return fields.filter(Boolean).join(' ').toLowerCase()
    },

    async loadKunden() {
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        }

        if (this.filterCity) params.city = this.filterCity
        if (this.filterPostalCode) params.postal_code = this.filterPostalCode

        // If we're searching, send the query to the backend
        if (this.searchQuery) {
          params.q = this.searchQuery
        }

        // If we're sorting, send the sort parameters to the backend
        if (this.sortField) {
          params.sort = this.sortField
          params.direction = this.sortDirection
        }

        const response = await this.fetchData({
          endpoint: 'kunden',
          params
        })

        // Handle the response based on its structure
        if (response && response.data) {
          this.rawKunden = response.data

          if (response.meta) {
            this.currentPage = response.meta.current_page || 1
            this.totalPages = response.meta.total_pages || 1
            this.totalCount = response.meta.total_count || 0
          }
        } else if (Array.isArray(response)) {
          this.rawKunden = response
          this.totalCount = response.length
          this.totalPages = 1
        } else {
          console.error('Unexpected API response structure:', response)
          this.rawKunden = []
        }

      } catch (error) {
        console.error('Error loading customers:', error)
        this.rawKunden = []
      }
    },

    handleSearch(query) {
      this.searchQuery = query
      this.currentPage = 1
      this.loadKunden()
    },

    handleSort({ field, direction }) {
      this.sortField = field
      this.sortDirection = direction
      this.loadKunden()
    },

    handlePageChange(page) {
      this.currentPage = page
      this.loadKunden()
    },

    handlePerPageChange(perPage) {
      this.perPage = perPage
      this.currentPage = 1
      this.loadKunden()
    }
  }
}
</script>

<style scoped>
.kunden-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.page-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.page-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: #111827;
}

.page-subtitle {
  margin: 0.125rem 0 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.header-right {
  display: flex;
  gap: 0.5rem;
}

.btn-new, .btn-clear {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
}

.btn-new {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.btn-new:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-clear:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.customer-link {
  color: #111827;
  text-decoration: none;
  transition: color 0.2s;
}

.customer-link:hover {
  color: #3b82f6;
  text-decoration: underline;
}

.filter-link {
  color: #3b82f6;
  text-decoration: none;
  cursor: pointer;
}

.filter-link:hover {
  text-decoration: underline;
}
</style>